import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../cn.js'
import { flagEmoji, type CountryOption } from '../countries.js'

export interface CountrySelectProps {
  label: string
  options: CountryOption[]
  /** ISO 3166-1 alpha-2 of the chosen country; `''` when nothing is chosen. */
  value: string
  onChange: (code: string) => void
  placeholder?: string
  hint?: ReactNode
  error?: string | undefined
  required?: boolean
  disabled?: boolean
  name?: string
  emptyText?: string
}

/**
 * The country of residence picker (PLAN.md §9.5, §11).
 *
 * Deliberately not a `Combobox`. That component is *label-valued* — the string
 * shown is the string reported — which is exactly right for the 77 Thai
 * provinces it was built for, and wrong here: the CRM wants `TH`, never
 * `Thailand`, and routing a code through a label-keyed control means two lookup
 * tables at every call site and a silent mismatch the day a name changes.
 *
 * A native `<select>` of 244 entries is the thing being replaced. On a phone it
 * is a scroll wheel with no way in but the first letter, on the first field a
 * new client ever touches.
 *
 * Search covers name, ISO code and dial code, so `Thailand`, `TH` and `66` all
 * land in the same place — people reach for whichever they know.
 */
export function CountrySelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  hint,
  error,
  required,
  disabled,
  name,
  emptyText = 'No matching country',
}: CountrySelectProps) {
  const id = useId()
  const listId = `${id}-list`
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const selected = useMemo(() => options.find((o) => o.code === value), [options, value])

  const [query, setQuery] = useState(selected?.name ?? '')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // The value can arrive after the options do — the CRM list replaces the
  // bundled one mid-session, and an IP guess arrives a moment after mount — so
  // the input tracks the resolved name, not the name at first render.
  useEffect(() => {
    if (selected) {
      setQuery(selected.name)
      return
    }
    // Losing the selection while the user is in the field means they are typing
    // over it, which clears the code by design; rewriting the box here would
    // delete the very characters that did the clearing. Only a clear from
    // outside — a reset form — should empty it.
    if (document.activeElement !== inputRef.current) setQuery('')
  }, [selected])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    // Reopening a finished field should show the whole list, not the one row
    // that happens to equal what is already in the box.
    if (!needle || needle === selected?.name.toLowerCase()) return options
    const digits = needle.replace(/^\+/, '')
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(needle) ||
        o.code.toLowerCase() === needle ||
        (digits !== '' && /^\d+$/.test(digits) && o.dialCode.startsWith(digits)),
    )
  }, [options, query, selected])

  function commit(option: CountryOption) {
    onChange(option.code)
    setQuery(option.name)
    setOpen(false)
    setActive(0)
  }

  /**
   * The one option a piece of free text can only mean, or nothing.
   *
   * This is what makes the field autofillable. Chrome, Safari and 1Password all
   * fill `autocomplete="country-name"` by writing a country *name* into the box
   * — they have no way to know this control reports a code — and without this
   * the form would sit there with the country visibly filled in and the
   * Continue button disabled, which reads as a broken page.
   *
   * Exact match only, on name or ISO code. A prefix would commit `Ind` to India
   * while the user was still typing their way to Indonesia.
   */
  function exactMatch(text: string): CountryOption | undefined {
    const needle = text.trim().toLowerCase()
    if (!needle) return undefined
    return options.find((o) => o.name.toLowerCase() === needle || o.code.toLowerCase() === needle)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (matches.length === 0 ? 0 : (i + step + matches.length) % matches.length))
      return
    }
    if (event.key === 'Enter' && open) {
      const choice = matches[active]
      if (choice) {
        // Swallowed only when it selects something, so a keyboard user who has
        // finished the field still gets a form submit from Enter.
        event.preventDefault()
        commit(choice)
      }
      return
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      setQuery(selected?.name ?? '')
      setOpen(false)
    }
  }

  function onBlur(event: React.FocusEvent<HTMLDivElement>) {
    // Clicking a row moves focus inside the widget; only leaving counts.
    if (rootRef.current?.contains(event.relatedTarget as Node | null)) return
    setOpen(false)

    // Someone who typed a country's full name and tabbed away has answered the
    // question, even though they never opened the list.
    const typed = exactMatch(query)
    if (typed && typed.code !== value) {
      commit(typed)
      return
    }
    // Anything else is free text, and free text is not a country.
    setQuery(selected?.name ?? '')
  }

  return (
    <div className="flex flex-col gap-1.5" ref={rootRef} onBlur={onBlur}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <div className="relative">
        {/* Decorative: the flag repeats the name beside it, so a screen reader
            announcing it twice would be noise. */}
        {selected && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg leading-none"
          >
            {flagEmoji(selected.code)}
          </span>
        )}
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${id}-opt-${active}` : undefined}
          autoComplete="country-name"
          value={query}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          onChange={(e) => {
            const text = e.target.value
            setQuery(text)
            setOpen(true)
            setActive(0)
            // A filled-in country arrives as one whole name in a single event,
            // and the password manager that wrote it will not open the list or
            // necessarily blur the field, so it has to resolve right here.
            //
            // By name only: matching the ISO code too would commit `IN` to
            // India on the second keystroke of "Indonesia", and the effect
            // above would then rewrite the box to "India" mid-word.
            const filled = options.find((o) => o.name.toLowerCase() === text.trim().toLowerCase())
            const next = filled?.code ?? ''
            // Typing over a chosen country clears it: the field must not keep
            // reporting a code the input no longer shows.
            if (next !== value) onChange(next)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={cn(
            'min-h-[44px] w-full rounded-lg border bg-white py-2 pr-3 text-ink',
            selected ? 'pl-10' : 'pl-3',
            'placeholder:text-muted-soft',
            error ? 'border-danger' : 'border-line-strong',
            'disabled:bg-surface-alt disabled:text-muted',
          )}
        />
        {/* The committed code, so this works inside a plain form submit too. */}
        {name && <input type="hidden" name={name} value={value} />}

        {open && (
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            className={cn(
              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-line-strong',
              'bg-white py-1 shadow-lg',
            )}
          >
            {matches.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted" role="presentation">
                {emptyText}
              </li>
            )}
            {matches.map((option, index) => (
              <li
                key={option.code}
                id={`${id}-opt-${index}`}
                role="option"
                aria-selected={option.code === value}
                // Pointer-down rather than click: click fires after blur, which
                // would have already reverted the input.
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(option)
                }}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-ink',
                  index === active && 'bg-surface-alt',
                  option.code === value && 'font-medium',
                )}
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  {flagEmoji(option.code)}
                </span>
                <span className="flex-1">{option.name}</span>
                <span className="text-muted" aria-hidden="true">
                  +{option.dialCode}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
